import React from 'react';
import Icon from '../../../components/AppIcon';


const LocalPartnerships = () => {
  const partnershipCategories = [
    {
      id: 1,
      category: "Utility Partners",
      icon: "Zap",
      description: "Strategic relationships with local utility companies for seamless infrastructure coordination",
      partners: [
        {
          name: "Regional Power & Light",
          type: "Electric Utility",
          services: "Pole attachment permits, underground coordination",
          coverage: "Northeast, Southeast regions"
        },
        {
          name: "Metro Gas & Electric",
          type: "Multi-Utility",
          services: "Joint trenching, utility locating services",
          coverage: "Midwest, Southwest regions"
        },
        {
          name: "Pacific Utilities Group",
          type: "Regional Consortium",
          services: "Environmental compliance, right-of-way access",
          coverage: "West Coast region"
        }
      ]
    },
    {
      id: 2,
      category: "Technology Providers",
      icon: "Cpu",
      description: "Partnerships with leading fiber optic and networking equipment manufacturers",
      partners: [
        {
          name: "Corning Incorporated",
          type: "Fiber Optic Solutions",
          services: "Premium fiber cables, connectivity hardware",
          coverage: "All regions"
        },
        {
          name: "CommScope Technologies",
          type: "Network Infrastructure",
          services: "Structured cabling, network equipment",
          coverage: "All regions"
        },
        {
          name: "Cisco Systems",
          type: "Networking Equipment",
          services: "Enterprise networking, security solutions",
          coverage: "All regions"
        }
      ]
    },
    {
      id: 3,
      category: "Business Organizations",
      icon: "Building",
      description: "Active membership in local business and industry associations",
      partners: [
        {
          name: "Regional Chamber of Commerce",
          type: "Business Association",
          services: "Local business networking, referral programs",
          coverage: "All major metropolitan areas"
        },
        {
          name: "Telecommunications Industry Association",
          type: "Industry Group",
          services: "Standards compliance, technical certification",
          coverage: "National coverage"
        },
        {
          name: "Local Contractors Alliance",
          type: "Trade Organization",
          services: "Subcontractor network, resource sharing",
          coverage: "Regional chapters"
        }
      ]
    },
    {
      id: 4,
      category: "Educational Institutions",
      icon: "GraduationCap",
      description: "Partnerships with technical schools and universities for workforce development",
      partners: [
        {
          name: "Regional Technical Institute",
          type: "Technical School",
          services: "Fiber optic technician training, certification programs",
          coverage: "Northeast, Southeast regions"
        },
        {
          name: "State University Engineering Dept",
          type: "University Partnership",
          services: "Research collaboration, internship programs",
          coverage: "Multiple state locations"
        },
        {
          name: "Community College Network",
          type: "Educational Consortium",
          services: "Continuing education, skills development",
          coverage: "All regions"
        }
      ]
    }
  ];

  const certifications = [
    {
      name: "BICSI Certified",
      description: "Building Industry Consulting Service International",
      icon: "Award",
      level: "Organizational Member"
    },
    {
      name: "NECA Contractor",
      description: "National Electrical Contractors Association",
      icon: "Shield",
      level: "Certified Member"
    },
    {
      name: "FOA Approved",
      description: "Fiber Optic Association Training Partner",
      icon: "CheckCircle",
      level: "Approved Training Center"
    },
    {
      name: "OSHA Compliant",
      description: "Occupational Safety and Health Administration",
      icon: "HardHat",
      level: "10-Hour & 30-Hour Certified"
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-brand-moderate overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary to-accent p-6 text-white">
        <h3 className="text-2xl font-bold mb-2">Local Partnerships & Certifications</h3>
        <p className="opacity-90">Building stronger communities through strategic collaborations</p>
      </div>
      <div className="p-6">
        {/* Partnership Categories */}
        <div className="space-y-8 mb-8">
          {partnershipCategories?.map((category) => (
            <div key={category?.id}>
              <div className="flex items-center space-x-3 mb-4">
                <div className="bg-primary/10 p-2 rounded-lg">
                  <Icon name={category?.icon} size={24} className="text-primary" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-text-primary">{category?.category}</h4>
                  <p className="text-sm text-text-secondary">{category?.description}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category?.partners?.map((partner, index) => (
                  <div key={index} className="bg-muted rounded-lg p-4 border border-border">
                    <h5 className="font-semibold text-text-primary mb-2">{partner?.name}</h5>
                    <div className="text-sm text-text-secondary mb-2">
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs">
                        {partner?.type}
                      </span>
                    </div>
                    <p className="text-sm text-text-secondary mb-2">{partner?.services}</p>
                    <div className="flex items-center space-x-1 text-xs text-text-secondary">
                      <Icon name="MapPin" size={12} />
                      <span>{partner?.coverage}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Certifications & Compliance */}
        <div className="border-t border-border pt-8">
          <h4 className="text-lg font-semibold text-text-primary mb-6 flex items-center">
            <Icon name="Award" size={20} className="mr-2" />
            Industry Certifications & Compliance
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {certifications?.map((cert, index) => (
              <div key={index} className="flex items-start space-x-4 p-4 bg-gradient-to-r from-accent/5 to-primary/5 rounded-lg border border-accent/20">
                <div className="bg-accent/10 p-2 rounded-lg">
                  <Icon name={cert?.icon} size={20} className="text-accent" />
                </div>
                <div>
                  <h5 className="font-semibold text-text-primary">{cert?.name}</h5>
                  <p className="text-sm text-text-secondary mb-1">{cert?.description}</p>
                  <span className="text-xs bg-accent/10 text-accent px-2 py-1 rounded">
                    {cert?.level}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Partnership Benefits */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-lg p-6 border border-primary/20">
            <h5 className="text-lg font-semibold text-text-primary mb-4 flex items-center">
              <Icon name="Handshake" size={20} className="mr-2" />
              Partnership Benefits for Our Clients
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Faster permit processing and approvals</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Access to premium equipment at competitive prices</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Coordinated utility work reduces project timelines</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Enhanced safety protocols and compliance</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Local business referral network access</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm text-text-secondary">Ongoing technical support and training</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocalPartnerships;